import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { getLabCases, getLabCrossDatasetLinks, getLabFusionHumanSerial, getLabFusionSerialEvidenceBundle, getLabUnitDetail } from '../api/smartiLabApi';
import LabBilingualCopy from '../components/LabBilingualCopy';
import LabCard from '../components/LabCard';
import LabEmptyState from '../components/LabEmptyState';
import LabMetric from '../components/LabMetric';
import LabTable from '../components/LabTable';
import SmartiLabBadge from '../components/SmartiLabBadge';
import SmartiLabIcon from '../components/SmartiLabIcon';
import SmartiLabScoreBar from '../components/SmartiLabScoreBar';
import { useSmartiLabContext } from '../SmartiLabContext';
import { getSmartiLabLocaleCopy, smartiLabHelpCopy } from '../content/smartiLabHelpCopy';

function formatReasons(value) {
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return (parsed || []).join(', ') || value || '—';
    } catch {
      return value || '—';
    }
  }
  return (value || []).join(', ') || '—';
}

function nearLimitMeasurementsToStory(rows = []) {
  const first = (rows || [])[0];
  if (!first) return [];
  return [{
    title: 'Near-limit warning',
    body: `${first.stepName || first.stepNumber || 'step'} at ${first.stationName || 'station'} reached ${Number(first.distancePercent || 0).toFixed(1)}% from the nearest limit.`,
    at: first.testStarted || '',
  }];
}

export default function UnitDetailPage() {
  const { i18n } = useTranslation();
  const { serialNumber } = useParams();
  const { sharedQuery } = useSmartiLabContext();
  const [detail, setDetail] = useState(null);
  const [fusionEvidence, setFusionEvidence] = useState(null);
  const [humanStory, setHumanStory] = useState(null);
  const [openCases, setOpenCases] = useState([]);
  const [linkRows, setLinkRows] = useState([]);
  const isItalian = i18n.language !== 'en';

  useEffect(() => {
    getLabUnitDetail(serialNumber, sharedQuery).then(setDetail);
    getLabFusionSerialEvidenceBundle(serialNumber).then(setFusionEvidence).catch(() => {});
    getLabFusionHumanSerial(serialNumber).then(setHumanStory).catch(() => {});
    getLabCases().then((rows) => setOpenCases((rows || []).filter((row) => row.linked_entity_key === serialNumber && row.status !== 'closed'))).catch(() => {});
    getLabCrossDatasetLinks({ ...sharedQuery, search: serialNumber }).then((payload) => setLinkRows((payload.rows || []).filter((row) => row.serialNumber === serialNumber))).catch(() => {});
  }, [serialNumber, sharedQuery]);

  const latestTest = detail?.tests?.[0] || null;
  const noBurnLinksCopy = getSmartiLabLocaleCopy(smartiLabHelpCopy.noBurnInLinks, i18n.language);
  const suspectRows = (fusionEvidence?.suspects?.length ? fusionEvidence.suspects : (detail?.tests || []).flatMap((test) => (test.fusionSuspects || []).map((suspect) => ({
    globalId: test.globalId,
    testStarted: test.testStarted,
    stationName: test.stationName,
    uutStatus: test.uutStatus,
    ...suspect,
  }))));
  const topSuspects = suspectRows.slice(0, 6);
  const topSuspect = topSuspects[0] || null;
  const humanRow = humanStory?.row || null;
  const humanSuspects = humanStory?.suspects || [];
  const topChecks = (() => {
    try {
      return JSON.parse(humanRow?.top_checks_json || '[]');
    } catch {
      return [];
    }
  })();
  const recommendedNextAction = openCases[0]
    ? `Continue the open case "${openCases[0].title}" and verify ${openCases[0].suspected_root_cause || 'the linked evidence'} before the next run.`
    : topSuspect
    ? `Start from ${topSuspect.stepName || topSuspect.stepNumber} and compare this failed unit against its closest passing twins.`
    : 'Review the latest failed session, confirm the burn-in link, and rerun Smarti Lab if the evidence is still thin.';
  const storyTimeline = [
    detail?.summary?.latestTestStarted ? { title: isItalian ? 'Failure point' : 'Failure point', body: latestTest?.stationName || '—', at: detail.summary.latestTestStarted } : null,
    nearLimitMeasurementsToStory(detail?.nearLimitMeasurements)?.[0] || null,
    linkRows[0] ? { title: isItalian ? 'Burn-in link' : 'Burn-in link', body: `${linkRows[0].burnInSourceLabel || '—'} • ${linkRows[0].statusBucket}`, at: linkRows[0].burnInLatestDate || linkRows[0].reviewedAt || linkRows[0].confirmedAt || '' } : null,
    topSuspect ? { title: isItalian ? 'Suspect ranking event' : 'Suspect ranking event', body: `${topSuspect.stepName || topSuspect.stepNumber} • ${Number(topSuspect.investigationPriority || topSuspect.investigation_priority || 0).toFixed(2)}`, at: topSuspect.testStarted || '' } : null,
    openCases[0] ? { title: isItalian ? 'Open case' : 'Open case', body: openCases[0].title, at: openCases[0].updated_at || openCases[0].created_at || '' } : null,
  ].filter(Boolean);
  const evidenceChannels = [
    [isItalian ? 'Punteggio evidenza' : 'Evidence score', topSuspect?.investigationPriority, 'primary'],
    [isItalian ? 'Confidenza' : 'Confidence', topSuspect?.suspectConfidence, 'success'],
    [isItalian ? 'Segnale temporale' : 'Temporal signal', topSuspect?.temporalSuspicion || topSuspect?.temporal_suspicion_score, 'primary'],
    [isItalian ? 'Anomalia' : 'Anomaly', topSuspect?.anomalyScore, 'warning'],
    [isItalian ? 'Pressione limite' : 'Limit pressure', topSuspect?.marginPressure, 'warning'],
    [isItalian ? 'Pass twin delta' : 'Pass twin delta', topSuspect?.pass_twin_delta_evidence, 'primary'],
    [isItalian ? 'Accordo modelli' : 'Model agreement', topSuspect?.model_agreement_score, 'success'],
    [isItalian ? 'Confidenza link burn-in' : 'Burn-in link confidence', topSuspect?.linkConfidence || topSuspect?.link_confidence, 'success'],
  ];

  return (
    <div className="lab-page">
      <section className="lab-page-hero">
        <div className="lab-page-hero-layout">
          <div className="lab-page-hero-meta">
            <div className="lab-page-hero-head">
              <span className="lab-page-icon"><SmartiLabIcon /></span>
              <div>
                <span className="lab-badge">{isItalian ? 'Forensic unit view' : 'Forensic unit view'}</span>
                <h2>{serialNumber}</h2>
              </div>
            </div>
            <p className="lab-page-hero-subtitle">
              {humanRow?.summary_it && humanRow?.summary_en
                ? (isItalian ? humanRow.summary_it : humanRow.summary_en)
                : (isItalian
                  ? 'Vista investigativa di una singola unità: primo guasto visibile, sospetti prioritari, burn-in, anomalie e contesto.'
                  : 'Investigation view for a single unit: first visible failure, priority suspects, burn-in, anomalies, and context.')}
            </p>
            <div className="lab-chip-row">
              <SmartiLabBadge variant={detail?.summary?.latestStatus === 'FAIL' ? 'danger' : 'success'} label={`${isItalian ? 'Stato' : 'Status'}: ${detail?.summary?.latestStatus ?? 'N/D'}`} />
              <SmartiLabBadge variant="neutral" label={`${isItalian ? 'Stazione' : 'Station'}: ${latestTest?.stationName || 'N/D'}`} />
              <SmartiLabBadge variant="neutral" label={`ATE: ${latestTest?.AteSwVersion || 'N/D'}`} />
              <SmartiLabBadge variant={(fusionEvidence?.links?.[0]?.link_confidence ?? detail?.fusionLinks?.[0]?.confidence) >= 0.75 ? 'burnLink' : 'warning'} label={`${isItalian ? 'Link burn-in' : 'Burn-in link'}: ${(fusionEvidence?.links?.[0]?.link_confidence ?? detail?.fusionLinks?.[0]?.confidence) != null ? Number(fusionEvidence?.links?.[0]?.link_confidence ?? detail?.fusionLinks?.[0]?.confidence).toFixed(2) : 'N/D'}`} />
            </div>
            <LabBilingualCopy
              it={humanRow?.summary_it || 'Questa pagina racconta la storia del guasto, cosa controllare prima e quali limiti di lettura richiedono prudenza.'}
              en={humanRow?.summary_en || 'This page tells the failure story, what to check first, and which reading limits require caution.'}
            />
          </div>
          <div className="lab-page-hero-panel">
            <div className="lab-hero-highlights">
              <div className="lab-hero-highlight">
                <strong>{isItalian ? 'Primo sospetto' : 'Top suspect'}</strong>
                <p>{topSuspect?.stepName || topSuspect?.stepNumber || (isItalian ? 'Dato non disponibile' : 'Data not available')}</p>
              </div>
              <div className="lab-hero-highlight">
                <strong>{isItalian ? 'Sorgente burn-in' : 'Burn-in source'}</strong>
                <p>{fusionEvidence?.links?.[0]?.source_label || latestTest?.fusionSuspects?.[0]?.linkedSourceLabel || detail?.fusionLinks?.[0]?.sourceLabel || (isItalian ? 'Dato non disponibile' : 'Data not available')}</p>
              </div>
              <div className="lab-hero-highlight">
                <strong>{isItalian ? 'Sessioni disponibili' : 'Available sessions'}</strong>
                <p>{detail?.summary?.totalTests ?? 0}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <LabCard
        title={isItalian ? 'Risposta principale' : 'Top answer'}
        description={<LabBilingualCopy it="Qui Smarti Lab traduce la sessione in una storia leggibile: cosa è successo, dove guardare prima e cosa resta da confermare." en="Here Smarti Lab translates the session into a readable story: what happened, where to look first, and what still needs confirmation." />}
      >
        <div className="lab-human-story-grid">
          <div className="lab-human-answer-card">
            <SmartiLabBadge variant="strict" label={topSuspect ? (isItalian ? 'Controlla prima' : 'Check first') : (isItalian ? 'In attesa dati' : 'Waiting for data')} />
            <strong>
              {humanRow
                ? (isItalian ? humanRow.summary_it : humanRow.summary_en)
                : (isItalian
                  ? `Questa unità ha fallito su ${topSuspect?.stepName || topSuspect?.stepNumber || 'un passaggio non disponibile'}.`
                  : `This unit failed at ${topSuspect?.stepName || topSuspect?.stepNumber || 'an unavailable step'}.`)}
            </strong>
            <p>{humanRow ? (isItalian ? humanRow.first_visible_failure_it : humanRow.first_visible_failure_en) : (isItalian ? 'Dati interpretati non ancora disponibili. Esegui una nuova analisi.' : 'Interpreted data is not available yet. Run a new analysis.')}</p>
          </div>
          <div className="lab-human-story-panel">
            <div className="lab-human-action-panel">
              <strong>{isItalian ? 'Segnale precedente al guasto visibile' : 'Signal before the visible failure'}</strong>
              <p>{humanRow ? (isItalian ? humanRow.upstream_summary_it : humanRow.upstream_summary_en) : (isItalian ? 'Nessuna lettura umana disponibile.' : 'No human-readable interpretation is available.')}</p>
            </div>
            <div className="lab-human-action-panel">
              <strong>{isItalian ? 'Possibili conseguenze' : 'Possible consequences'}</strong>
              <p>{humanRow ? (isItalian ? humanRow.downstream_summary_it : humanRow.downstream_summary_en) : '—'}</p>
            </div>
          </div>
        </div>
      </LabCard>

      <LabCard
        title={isItalian ? 'Prossima azione consigliata' : 'Recommended next action'}
        description={<LabBilingualCopy it="Questa sezione chiude il loop operativo: cosa fare adesso, quali link sono affidabili e se esiste già un caso aperto." en="This section closes the operational loop: what to do now, which links are trustworthy, and whether there is already an open case." />}
      >
        <div className="lab-grid two-col">
          <div className="lab-human-action-panel">
            <strong>{isItalian ? 'Cosa fare ora' : 'What to do now'}</strong>
            <p>{recommendedNextAction}</p>
            <div className="lab-chip-row">
              <SmartiLabBadge variant={openCases.length ? 'warning' : 'success'} label={`${isItalian ? 'Casi aperti' : 'Open cases'}: ${openCases.length}`} />
              <SmartiLabBadge variant={linkRows[0]?.statusBucket === 'confirmed' ? 'success' : linkRows[0]?.statusBucket === 'missing' ? 'danger' : 'warning'} label={`${isItalian ? 'Stato link' : 'Link status'}: ${linkRows[0]?.statusBucket || 'unknown'}`} />
            </div>
            {openCases[0]?.linked_entity_key ? <Link className="lab-inline-button" to="/smarti-lab/cases">Open case workflow</Link> : null}
          </div>
          <div className="lab-human-action-panel">
            <strong>{isItalian ? 'Timeline investigativa' : 'Investigation timeline'}</strong>
            {storyTimeline.length ? storyTimeline.map((item, index) => (
              <p key={`${item.title}-${index}`}>
                <strong>{item.title}</strong>
                {item.at ? ` • ${item.at}` : ''}
                <br />
                {item.body}
              </p>
            )) : <p>{isItalian ? 'Timeline non ancora disponibile.' : 'No timeline available yet.'}</p>}
          </div>
        </div>
      </LabCard>

      <LabCard
        title={isItalian ? 'Controlli consigliati' : 'Recommended checks'}
        description={<LabBilingualCopy it="Queste carte dicono cosa controllare prima, cosa controllare dopo e cosa monitorare." en="These cards show what to check first, what to check next, and what to monitor." />}
      >
        {(topChecks.length || humanSuspects.length) ? (
          <div className="lab-human-priority-grid">
            {(topChecks.length ? topChecks : humanSuspects.slice(0, 3).map((suspect, index) => ({
              rank: index + 1,
              step_name: suspect.step_name,
              action_label_it: suspect.human_priority_label_it,
              action_label_en: suspect.human_priority_label_en,
              reason_it: suspect.main_sentence_it,
              reason_en: suspect.main_sentence_en,
              confidence_label_it: suspect.confidence_label_it,
              confidence_label_en: suspect.confidence_label_en,
              caution_it: suspect.caution_explanation_it,
              caution_en: suspect.caution_explanation_en,
            }))).map((check) => (
              <article key={`${check.rank}-${check.step_name}`} className="lab-human-priority-card">
                <div className="lab-human-priority-head">
                  <div className="lab-data-stack">
                    <span className="lab-badge">{check.rank}</span>
                    <strong>{check.step_name}</strong>
                    <p>{isItalian ? check.reason_it : check.reason_en}</p>
                  </div>
                  <div className="lab-chip-row">
                    <SmartiLabBadge variant="strict" label={isItalian ? check.action_label_it : check.action_label_en} />
                    <SmartiLabBadge variant="success" label={isItalian ? check.confidence_label_it : check.confidence_label_en} />
                  </div>
                </div>
                <div className="lab-human-action-panel">
                  <strong>{isItalian ? 'Cautela' : 'Caution'}</strong>
                  <p>{isItalian ? check.caution_it : check.caution_en}</p>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <LabEmptyState
            title={isItalian ? 'Controlli leggibili non disponibili' : 'Readable checks are not available'}
            description={isItalian ? 'Dati interpretati non ancora disponibili. Esegui una nuova analisi.' : 'Interpreted data is not available yet. Run a new analysis.'}
          />
        )}
      </LabCard>

      <details className="lab-technical-details">
        <summary>{isItalian ? 'Mostra dettagli tecnici' : 'Show technical details'}</summary>
        <div className="lab-technical-stack">

      <LabCard
        title={`${isItalian ? 'Indagine unità' : 'Unit investigation'}: ${serialNumber}`}
        description={<LabBilingualCopy it="Questa pagina mostra l’indagine di una singola unità: timeline dei passaggi, primo guasto visibile, sospetti principali, confronto con unità passate simili, segnali burn-in e anomalie di misura." en="This page shows the investigation for one unit: step timeline, first visible failure, top suspects, comparison with similar passing units, burn-in signals, and measurement anomalies." />}
        help={{ ...smartiLabHelpCopy.unitDetail, ariaLabel: 'Spiegazione dettaglio unità / Unit detail explanation' }}
      >
        <div className="lab-metric-grid">
          <LabMetric label={isItalian ? 'Test' : 'Tests'} value={detail?.summary?.totalTests ?? 0} />
          <LabMetric label="Fail" value={detail?.summary?.failCount ?? 0} />
          <LabMetric label={isItalian ? 'Ultimo stato' : 'Latest status'} value={detail?.summary?.latestStatus ?? '—'} />
          <LabMetric label={isItalian ? 'Ultima stazione' : 'Latest station'} value={latestTest?.stationName ?? '—'} />
          <LabMetric label={isItalian ? 'Sorgente burn-in collegata' : 'Linked burn-in source'} value={fusionEvidence?.links?.[0]?.source_label || latestTest?.fusionSuspects?.[0]?.linkedSourceLabel || detail?.fusionLinks?.[0]?.sourceLabel || '—'} />
          <LabMetric label={isItalian ? 'Confidenza link' : 'Link confidence'} value={(fusionEvidence?.links?.[0]?.link_confidence ?? detail?.fusionLinks?.[0]?.confidence) != null ? Number(fusionEvidence?.links?.[0]?.link_confidence ?? detail?.fusionLinks?.[0]?.confidence).toFixed(2) : '—'} help={{ ...smartiLabHelpCopy.burnLinkConfidence, ariaLabel: 'Spiegazione confidenza link burn-in / Burn-in link confidence explanation' }} />
        </div>
      </LabCard>

      <LabCard
        title={isItalian ? 'Timeline sessioni' : 'Session timeline'}
        description={<LabBilingualCopy it="Ogni sessione mostra il sospetto con più evidenza, la relazione temporale con il primo guasto e i principali motivi del ranking." en="Each session shows the highest-evidence suspect, its temporal relationship to the first failure, and the main reasons behind the ranking." />}
        help={{ ...smartiLabHelpCopy.firstVisibleFailure, ariaLabel: 'Spiegazione primo guasto visibile / First visible failure explanation' }}
      >
        {(detail?.tests || []).length ? (
          <div className="lab-timeline-grid">
            {(detail?.tests || []).slice(0, 8).map((test) => {
              const top = (test.fusionSuspects || [])[0];
              return (
                <article key={test.globalId || test.testStarted} className="lab-timeline-card">
                  <div className="lab-chip-row">
                    <SmartiLabBadge variant={test.uutStatus === 'FAIL' ? 'danger' : 'success'} label={test.uutStatus || 'N/D'} />
                    <SmartiLabBadge variant="neutral" label={test.stationName || (isItalian ? 'Stazione N/D' : 'Station N/A')} />
                  </div>
                  <div className="lab-timeline-step">
                    <strong>{test.testStarted || (isItalian ? 'Avvio non disponibile' : 'Start not available')}</strong>
                    <p>{[test.AteSwVersion, test.Testspec].filter(Boolean).join(' • ') || (isItalian ? 'Contesto non disponibile' : 'Context not available')}</p>
                    <p>{top ? `${top.rank}. ${top.stepName || top.stepNumber}` : (isItalian ? 'Nessun sospetto ML associato' : 'No ML suspect attached')}</p>
                    <div className="lab-chip-row">
                      {top ? <SmartiLabBadge variant="primary" label={`${isItalian ? 'Priorità' : 'Priority'} ${top.investigationPriority}`} /> : null}
                      {top ? <SmartiLabBadge variant="burnLink" label={`${isItalian ? 'Confidenza' : 'Confidence'} ${top.suspectConfidence}`} /> : null}
                    </div>
                    <p className="lab-helper-text">{top ? formatReasons(top.evidenceReasons) : (isItalian ? 'Nessun motivo dettagliato disponibile.' : 'No detailed reasons available.')}</p>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <LabEmptyState
            title={isItalian ? 'Nessuna sessione di produzione per questo seriale' : 'No production session for this serial'}
            description={isItalian ? 'Nessuno storico di produzione trovato per questo seriale nello scope attuale.' : 'No production history was found for this serial under the current scope.'}
          />
        )}
      </LabCard>

      <div className="lab-grid two-col">
        <LabCard
          title={isItalian ? 'Carte sospetti principali' : 'Top suspect cards'}
          description={<LabBilingualCopy it="Qui vedi le righe con l’evidenza più forte per questo seriale, inclusi segnali temporali, anomalia di misura e pressione verso i limiti." en="Here you see the rows with the strongest evidence for this serial, including temporal signals, measurement anomaly, and limit pressure." />}
          help={{ ...smartiLabHelpCopy.evidenceScore, ariaLabel: 'Spiegazione punteggio di evidenza / Evidence score explanation' }}
        >
          {topSuspects.length ? (
            <div className="lab-suspect-grid">
              {topSuspects.map((suspect, index) => (
                <article key={`${suspect.globalId}-${suspect.stepNumber}-${index}`} className="lab-suspect-card">
                  <div className="lab-suspect-head">
                    <div className="lab-data-stack">
                      <span className="lab-badge">#{index + 1}</span>
                      <strong>{suspect.stepName || suspect.stepNumber || (isItalian ? 'Dato non disponibile' : 'Data not available')}</strong>
                      <p>{suspect.testStarted || (isItalian ? 'Avvio non disponibile' : 'Start not available')}</p>
                    </div>
                    <div className="lab-chip-row">
                      <SmartiLabBadge variant="primary" label={`${isItalian ? 'Priorità' : 'Priority'} ${suspect.investigationPriority ?? suspect.investigation_priority ?? '—'}`} />
                      <SmartiLabBadge variant="success" label={`${isItalian ? 'Confidenza' : 'Confidence'} ${suspect.suspectConfidence ?? suspect.suspect_confidence ?? '—'}`} />
                    </div>
                  </div>
                  <div className="lab-score-grid">
                    <SmartiLabScoreBar label={isItalian ? 'Segnale temporale' : 'Temporal signal'} value={suspect.temporalSuspicion ?? suspect.temporal_suspicion_score} />
                    <SmartiLabScoreBar label={isItalian ? 'Anomalia' : 'Anomaly'} value={suspect.anomalyScore ?? suspect.anomaly_score} variant="warning" />
                    <SmartiLabScoreBar label={isItalian ? 'Pass twin delta' : 'Pass twin delta'} value={suspect.pass_twin_delta_evidence} variant="primary" />
                    <SmartiLabScoreBar label={isItalian ? 'Accordo modelli' : 'Model agreement'} value={suspect.model_agreement_score} variant="success" />
                  </div>
                  <p className="lab-helper-text">{formatReasons(suspect.evidenceReasons || suspect.evidence_reasons)}</p>
                </article>
              ))}
            </div>
          ) : (
            <LabEmptyState
              title={isItalian ? 'Nessuna riga sospetta' : 'No suspect row'}
              description={isItalian ? 'Il database Fusion non contiene ancora righe sospette ML per questo seriale.' : 'The Fusion database does not currently contain ML suspect rows for this serial.'}
            />
          )}
        </LabCard>
        <LabCard
          title={isItalian ? 'Bundle evidenze' : 'Evidence bundle'}
          description={<LabBilingualCopy it="Spiega perché il sistema considera forte un sospetto e quali limiti di lettura richiedono prudenza." en="Explains why the system considers a suspect strong and which reading limits require caution." />}
          help={{ ...smartiLabHelpCopy.cautionFlags, ariaLabel: 'Spiegazione flag di cautela / Caution flags explanation' }}
        >
          {topSuspect ? (
            <div className="lab-evidence-grid">
              <div className="lab-evidence-bundle">
                {evidenceChannels.map(([label, value, variant]) => (
                  <SmartiLabScoreBar key={label} label={label} value={value} variant={variant} />
                ))}
              </div>
              <div className="lab-evidence-bundle">
                <div className="lab-evidence-row">
                  <strong>{isItalian ? 'Motivi principali' : 'Main reasons'}</strong>
                  <p>{formatReasons(topSuspect.evidenceReasons)}</p>
                </div>
                <div className="lab-evidence-row">
                  <strong>{isItalian ? 'Flag di cautela' : 'Caution flags'}</strong>
                  <p>{formatReasons(topSuspect.cautionFlags)}</p>
                </div>
                <div className="lab-evidence-row">
                  <strong>{isItalian ? 'Sorgente burn-in' : 'Burn-in source'}</strong>
                  <p>{topSuspect.linkedSourceLabel || (isItalian ? 'Dato non disponibile' : 'Data not available')}</p>
                </div>
              </div>
            </div>
          ) : (
            <LabEmptyState
              title={isItalian ? 'Nessun dettaglio evidenza' : 'No evidence detail'}
              description={isItalian ? 'Nessuna evidenza ML dettagliata disponibile per questo seriale.' : 'No detailed ML suspect evidence is available for this serial yet.'}
            />
          )}
        </LabCard>
      </div>

      <div className="lab-grid two-col">
        <LabCard
          title={isItalian ? 'Righe con anomalia di misura' : 'Measurement anomaly rows'}
          description={<LabBilingualCopy it="Mostra misure di produzione vicine ai limiti o fuori distribuzione storica. Sono segnali utili, ma non equivalgono automaticamente a un guasto." en="Shows production measurements that are near limits or outside the historical distribution. They are useful signals, but they do not automatically mean failure." />}
          help={{ ...smartiLabHelpCopy.anomalyScore, ariaLabel: 'Spiegazione punteggio di anomalia / Anomaly score explanation' }}
        >
          <LabTable
            rows={detail?.nearLimitMeasurements || []}
            emptyTitle={isItalian ? 'Nessuna misura near-limit' : 'No near-limit measurement'}
            emptyDescription={isItalian ? 'Nessuna misura di produzione vicina ai limiti trovata per questo seriale.' : 'No near-limit production measurements were found for this serial.'}
            columns={[
              { key: 'testStarted', label: isItalian ? 'Avvio' : 'Start' },
              { key: 'stepName', label: isItalian ? 'Nome step' : 'Step name' },
              { key: 'value', label: 'Value' },
              { key: 'distancePercent', label: isItalian ? 'Distanza %' : 'Distance %' },
              { key: 'stationName', label: isItalian ? 'Stazione' : 'Station' },
            ]}
          />
        </LabCard>
        <LabCard
          title={isItalian ? 'Link burn-in usati' : 'Burn-in links used'}
          description={<LabBilingualCopy it="Questi sono i collegamenti usati quando il burn-in è disponibile. Se il link è ambiguo o poco credibile, il contributo burn-in pesa meno nel ranking finale." en="These are the links used when burn-in is available. If the link is ambiguous or not very credible, burn-in contributes less to the final ranking." />}
          help={{ ...smartiLabHelpCopy.burnLinkConfidence, ariaLabel: 'Spiegazione link burn-in / Burn-in link explanation' }}
        >
          <LabTable
            rows={(fusionEvidence?.links || detail?.fusionLinks || []).map((row) => ({
              sourceLabel: row.sourceLabel || row.source_label,
              score: row.score || row.link_score,
              confidence: row.confidence || row.link_confidence,
              reason: row.reason || row.link_reason,
              ambiguityCount: row.ambiguityCount || row.ambiguity_count,
            }))}
            emptyTitle={noBurnLinksCopy.title}
            emptyDescription={noBurnLinksCopy.body}
            columns={[
              { key: 'sourceLabel', label: isItalian ? 'Sorgente burn-in' : 'Burn-in source' },
              { key: 'score', label: isItalian ? 'Score link' : 'Link score' },
              { key: 'confidence', label: isItalian ? 'Confidenza' : 'Confidence' },
              { key: 'reason', label: isItalian ? 'Motivo' : 'Reason' },
              { key: 'ambiguityCount', label: isItalian ? 'Ambiguità' : 'Ambiguity' },
            ]}
          />
        </LabCard>
      </div>

      <div className="lab-grid two-col">
        <LabCard
          title={isItalian ? 'Confronto con unità sane' : 'Pass twin comparison'}
          description={<LabBilingualCopy it={humanRow?.pass_twin_summary_it || 'Se il dataset non espone ancora i delta pass-twin, questa area resta come pannello operativo pronto a leggere il confronto appena disponibile.'} en={humanRow?.pass_twin_summary_en || 'If the dataset does not expose pass-twin deltas yet, this area remains an operational panel ready to show the comparison as soon as it is available.'} />}
          help={{ ...smartiLabHelpCopy.passTwinComparison, ariaLabel: 'Spiegazione confronto con unità sane / Pass twin comparison explanation' }}
        >
          <div className="lab-twin-panel">
            <p>{isItalian ? 'Confronto pass-twin non disponibile nei dati restituiti da questa sessione. Il resto del ranking usa comunque evidenze temporali, anomalia, limiti e burn-in.' : 'Pass-twin comparison is not available in the data returned for this session. The rest of the ranking still uses temporal, anomaly, limit, and burn-in evidence.'}</p>
          </div>
        </LabCard>
        <LabCard
          title={isItalian ? 'Deriva e famiglia di guasto' : 'Drift and failure family'}
          description={<LabBilingualCopy it="Questa area raccoglie il contesto più ampio del caso: cluster, pattern simili e segnali recenti di deriva." en="This area collects the broader context of the case: clusters, similar patterns, and recent drift signals." />}
          help={{ ...smartiLabHelpCopy.failureFamilies, ariaLabel: 'Spiegazione famiglie di guasto / Failure family explanation' }}
        >
          <div className="lab-evidence-bundle">
            <div className="lab-evidence-row">
              <strong>{isItalian ? 'Famiglia / cluster' : 'Family / cluster'}</strong>
              <p>{topSuspect?.clusterId || (isItalian ? 'Dato non disponibile' : 'Data not available')}</p>
            </div>
            <div className="lab-evidence-row">
              <strong>{isItalian ? 'Contesto drift' : 'Drift context'}</strong>
              <p>{isItalian ? 'I segnali di deriva non sono presenti nel payload di dettaglio corrente.' : 'Drift signals are not present in the current detail payload.'}</p>
            </div>
          </div>
        </LabCard>
      </div>
        </div>
      </details>
    </div>
  );
}
